import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { StoreCustomer } from '@/types/models/Customer';
import { CustomerService } from '@/services';
import { logger } from '@/lib/logger';

interface CustomersState {
  customers: StoreCustomer[];
  isLoading: boolean;
  error: string | null;
  storeId: string | null;
  isLoaded: boolean;
  searchQuery: string;
}

const initialState: CustomersState = {
  customers: [],
  isLoading: false,
  error: null,
  storeId: null,
  isLoaded: false,
  searchQuery: '',
};

export const fetchCustomers = createAsyncThunk(
  'customers/fetchAll',
  async (storeId: string) => {
    logger.info('Fetching all customers', { storeId });
    return CustomerService.getAll(storeId);
  }
);

export const createCustomer = createAsyncThunk(
  'customers/create',
  async (data: { storeId: string; customerName: string; customerPhone: string; updateName: boolean; language_locale?: string }) => {
    logger.info('Creating customer', { ...data });
    return CustomerService.create(data);
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/delete',
  async ({ storeId, id }: { storeId: string; id: string }) => {
    logger.info('Deleting customer', { storeId, id });
    await CustomerService.delete(storeId, id);
    return id;
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/update',
  async ({ id, data }: { id: string; data: { customerName: string } }) => {
    logger.info('Updating customer', { id, data });
    return CustomerService.update(id, data);
  }
);

export const toggleCustomerActive = createAsyncThunk(
  'customers/toggleActive',
  async ({ storeId, id, isActive }: { storeId: string; id: string; isActive: boolean }) => {
    logger.info('Toggling customer active status', { storeId, id, isActive });
    return CustomerService.toggleActive(storeId, id, isActive);
  }
);

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomers: (state) => {
      state.customers = [];
      state.storeId = null;
      state.isLoaded = false;
      state.searchQuery = '';
    },
    addCustomer: (state, action: PayloadAction<StoreCustomer>) => {
      state.customers.unshift(action.payload);
    },
    updateCustomerLocal: (state, action: PayloadAction<StoreCustomer>) => {
      const idx = state.customers.findIndex(c => c.id === action.payload.id);
      if (idx >= 0) state.customers[idx] = action.payload;
    },
    deleteCustomerLocal: (state, action: PayloadAction<string>) => {
      state.customers = state.customers.filter(c => c.id !== action.payload);
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.storeId = action.meta.arg;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload;
        state.isLoaded = true;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch customers';
        state.isLoaded = false;
        logger.error('Failed to fetch customers', { error: action.error });
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.customers.unshift(action.payload);
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.customers = state.customers.filter(c => c.id !== action.payload);
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const idx = state.customers.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) state.customers[idx] = action.payload;
      })
      .addCase(toggleCustomerActive.fulfilled, (state, action) => {
        const idx = state.customers.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) state.customers[idx] = action.payload;
      });
  },
});

export const { clearCustomers, addCustomer, updateCustomerLocal, deleteCustomerLocal, setSearchQuery } = customersSlice.actions;

export const selectCustomerByPhone = (customers: StoreCustomer[], phone: string): StoreCustomer | null => {
  return customers.find(customer => customer.customer_phone === phone) || null;
};

export default customersSlice.reducer; 